import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Enums as CoreEnums, metaData, cache } from "@cornerstonejs/core";
import { Enums as ToolsEnums, WindowLevelTool } from "@cornerstonejs/tools";
import { getGlobalRenderingEngine, attachWebGLRecovery, ensureGpuRenderingEngine, recycleRenderingEngine } from "../context/renderingEngine";
import { setActiveViewportId } from "../context/viewportService";
import { createToolGroup, getMPRToolGroup, getStackToolGroup, removeViewportFromToolGroups, setCrosshairsEnabled } from "../context/toolGroup";
import { loadSharedSeriesVolume, isVolumeLoadCurrent, cancelPendingVolumeLoads, detachVolumeFromEngine, releaseCurrentVolume, safeResetCamera, startVolumePixelLoad, waitForLoadedFrames, getCurrentSeriesKey } from "../context/volumeManager";
import { buildImageIds, getViewportOrientation, getPatientInfo, getSliceCount } from "./utils/viewerHelpers";
import { sortImageIdsByPatientPosition } from "./utils/sortImageIds";
import { PanelOverlayTools } from "../tools/PanelOverlayTools";
import { getSeriesById } from "./Series";
import "../../styles/ViewerCanvas.css";

const MIN_SLICES_FOR_MPR = 4;
const DEFAULT_LAYOUT = { rows: 1, cols: 3 };
const sortCache = new Map();

function isMPRMode(activeTool, mprView) {
  return (
    mprView === "THREE_VIEW" ||
    mprView === "AXIAL" ||
    mprView === "SAGITTAL" ||
    mprView === "CORONAL" ||
    activeTool === "MPRTool" ||
    activeTool === "MPR_THREE_VIEW" ||
    activeTool === "MPR_AXIAL" ||
    activeTool === "MPR_SAGITTAL" ||
    activeTool === "MPR_CORONAL"
  );
}

function getSeriesKey(series) {
  if (!series) return null;
  return String(
    series.seriesInstanceUID ||
      series.id ||
      series.seriesId ||
      series.seriesNumber ||
      ""
  );
}

async function sortImageIdsAsync(imageIds) {
  if (!imageIds?.length) return [];

  const cacheKey = `${imageIds[0]}_${imageIds.length}`;
  if (sortCache.has(cacheKey)) {
    return sortCache.get(cacheKey);
  }

  if (imageIds.length > 40) {
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  const result = sortImageIdsByPatientPosition(imageIds);
  sortCache.set(cacheKey, result);
  if (sortCache.size > 20) {
    const firstKey = sortCache.keys().next().value;
    sortCache.delete(firstKey);
  }
  return result;
}
function ViewportPanel({ selectedSeries, seriesIndex, panelIndex, onBack, isFirst, mprView, isMPR, activeTool, layout, onSeriesDrop, sharedVolumeId, volumeLoading, mprReady, mprBlocked, loadNote, onElementReady }) {
  const elementRef = useRef(null);
  const imageIdsRef = useRef([]);
  const loadGenerationRef = useRef(0);
  const movingRef = useRef(false);
  const sliceTextRef = useRef(null);
  const viewportId = `VIEWPORT_${panelIndex}`;
  const [loading, setLoading] = useState(false);
  const [totalSlices, setTotalSlices] = useState(0);
  const [imageSize, setImageSize] = useState("");
  const [thickness, setThickness] = useState("");
  const [patientInfo, setPatientInfo] = useState({
    patientId: "",
    patientName: "",
    studyDesc: "",
    seriesDesc: "",
    windowCenter: "",
    windowWidth: "",
  });

  const seriesKey = getSeriesKey(selectedSeries);
  const showLoader = loading || (isMPR && (volumeLoading || Boolean(loadNote)));

  const moveMPRSlice = useCallback((viewport, direction) => {
    if (!viewport || typeof viewport.getCamera !== "function" || movingRef.current) {
      return;
    }
    movingRef.current = true;
    requestAnimationFrame(() => {
      try {
        const camera = viewport.getCamera();
        if (!camera?.viewPlaneNormal) return;
        const spacing = viewport.getImageData?.()?.spacing?.[2] || 1;
        const step = direction * spacing;
        const normal = camera.viewPlaneNormal;
        viewport.setCamera({
          position: camera.position.map((value, index) => value + normal[index] * step),
          focalPoint: camera.focalPoint.map(
            (value, index) => value + normal[index] * step
          ),
        });
        viewport.render();
      } finally {
        movingRef.current = false;
      }
    });
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      try {
        const data = JSON.parse(event.dataTransfer.getData("text/plain"));
        const seriesData = data?.seriesId
          ? getSeriesById(data.seriesId) || data
          : data;
        if (seriesData?.imageIds) {
          onSeriesDrop(seriesData);
        }
      } catch (error) {
        console.error("Invalid drop data:", error);
      }
    },
    [onSeriesDrop]
  );

  const updateMetadata = useCallback(
    (viewport, generation) => {
      if (!viewport || generation !== loadGenerationRef.current) return;
      const imageId =
        !isMPR || viewport.type !== CoreEnums.ViewportType.ORTHOGRAPHIC
          ? viewport.getCurrentImageId?.()
          : imageIdsRef.current?.[0];
      if (!imageId) return;
      const info = getPatientInfo(imageId);
      setImageSize(info.imageSize || "");
      setThickness(info.sliceThickness || "");
      setPatientInfo({
        patientId: info.patientId || "",
        patientName: info.patientName || "",
        studyDesc: info.studyDesc || "",
        seriesDesc: info.seriesDesc || "",
        windowCenter: info.windowCenter ?? "",
        windowWidth: info.windowWidth ?? "",
      });
    },
    [isMPR]
  );

  useEffect(() => {
    if (isMPR) return undefined;
    let cancelled = false;
    const initialize = async () => {
      if (!elementRef.current) return;
      try {
        const engine = await getGlobalRenderingEngine();
        if (cancelled || !elementRef.current) return;
        const existing = engine.getViewport(viewportId);
        if (existing && existing.type !== CoreEnums.ViewportType.STACK) {
          removeViewportFromToolGroups(viewportId);
          engine.disableElement(viewportId);
        }
        if (!engine.getViewport(viewportId)) {
          engine.enableElement({
            viewportId,
            type: CoreEnums.ViewportType.STACK,
            element: elementRef.current,
            defaultOptions: { background: [0, 0, 0] },
          });
        }
        const viewport = engine.getViewport(viewportId);
        if (!viewport || cancelled) return;
        viewport.setOptions({ backgroundColor: [0, 0, 0] }, true);
        attachWebGLRecovery(engine, viewportId);
        createToolGroup(viewportId, engine.id, false);
        engine.resize(true, false);
      } catch (error) {
        if (!cancelled) {
          console.error("Viewport initialization failed:", viewportId, error);
        }
      }
    };
    initialize();
    return () => {
      cancelled = true;
      getGlobalRenderingEngine()
        .then((engine) => {
          try {
            const viewport = engine.getViewport(viewportId);
            if (viewport && viewport.type === CoreEnums.ViewportType.STACK) {
              removeViewportFromToolGroups(viewportId);
              engine.disableElement(viewportId);
            }
          } catch {
            // Unmount race.
          }
        })
        .catch(() => {});
    };
  }, [viewportId, isMPR]);

  useEffect(() => {
    let cancelled = false;
    const resize = async () => {
      try {
        const engine = await getGlobalRenderingEngine();
        if (cancelled) return;
        const viewport = engine.getViewport(viewportId);
        if (!viewport) return;
        engine.resize(true, true);
        viewport.render();
      } catch {
        // Ignore resize races.
      }
    };
    resize();
    return () => {
      cancelled = true;
    };
  }, [viewportId, layout.rows, layout.cols]);

  const setStackWithRetry = useCallback(
    async (viewport, imageIds, index, maxAttempts = 3) => {
      let lastError;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          await viewport.setStack(imageIds, index);
          return;
        } catch (error) {
          lastError = error;
          imageIds.forEach((imageId) => {
            try {
              cache.removeImageLoadObject(imageId);
            } catch {
              // Not cached, or already removed — safe to ignore.
            }
          });
          if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
          }
        }
      }
      throw lastError;
    },
    []
  );

  useEffect(() => {
    if (isMPR) return undefined;
    if (!selectedSeries?.imageIds?.length) return undefined;

    const generation = ++loadGenerationRef.current;
    let cancelled = false;

    const isCurrent = () =>
      !cancelled && loadGenerationRef.current === generation;

    const loadStack = async () => {
      setLoading(true);
      try {
        const engine = await getGlobalRenderingEngine();
        if (!isCurrent()) return;
        let viewport = engine.getViewport(viewportId);
        let attempts = 0;
        while (!viewport && attempts < 40 && isCurrent()) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          viewport = engine.getViewport(viewportId);
          attempts += 1;
        }
        if (!viewport || viewport.type !== CoreEnums.ViewportType.STACK || !isCurrent()) {
          return;
        }
        const rawImageIds = buildImageIds(selectedSeries.imageIds);
        const sortedImageIds = await sortImageIdsAsync(rawImageIds);
        if (!isCurrent()) return;
        imageIdsRef.current = sortedImageIds;
        const totalPanelsInLayout = Math.max(
          1,
          (layout?.rows || 1) * (layout?.cols || 1)
        );
        const sliceCount = sortedImageIds.length;
        const startIndex =
          totalPanelsInLayout > 1 && sliceCount > 0
            ? Math.min(
                sliceCount - 1,
                Math.floor((panelIndex * sliceCount) / totalPanelsInLayout)
              )
            : 0;

        await setStackWithRetry(viewport, sortedImageIds, startIndex);
        if (!isCurrent()) return;

        setTotalSlices(sortedImageIds.length);
        viewport.resetCamera();
        viewport.render();
        updateMetadata(viewport, generation);
      } catch (error) {
        if (isCurrent()) {
          console.error(`[Panel ${panelIndex}] stack loading failed:`, error);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    loadStack();
    return () => {
      cancelled = true;
    };
  },[isMPR, seriesKey, selectedSeries, viewportId, panelIndex, updateMetadata, layout.rows, layout.cols]);

  useEffect(() => {
    if (!isMPR || !sharedVolumeId || !mprReady) return undefined;
    const generation = ++loadGenerationRef.current;
    const volume = cache.getVolume(sharedVolumeId);
    const imageIds = volume?.imageIds || selectedSeries?.imageIds || [];
    imageIdsRef.current = imageIds;
    const orientation = getViewportOrientation(mprView, panelIndex);
    setTotalSlices(getSliceCount(volume, orientation) || volume?.dimensions?.[2] || imageIds.length);

    let cancelled = false;
    (async () => {
      try {
        const engine = await getGlobalRenderingEngine();
        if (cancelled) return;
        const viewport = engine.getViewport(viewportId);
        if (viewport) updateMetadata(viewport, generation);
      } catch {
        // Metadata is optional.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isMPR, sharedVolumeId, mprReady, mprView, viewportId, panelIndex, selectedSeries, updateMetadata
  ]);

  useEffect(() => {
    let cancelled = false;
    const bindTools = async () => {
      const engine = await getGlobalRenderingEngine();
      if (cancelled) return;
      const viewport = engine.getViewport(viewportId);
      if (!viewport) return;
      const isOrthographic = viewport.type === CoreEnums.ViewportType.ORTHOGRAPHIC;
      const toolGroup = isOrthographic ? getMPRToolGroup() : getStackToolGroup();
      if (!toolGroup) return;
      createToolGroup(viewportId, engine.id, isOrthographic);
      if (isOrthographic && !mprReady) {
        setCrosshairsEnabled(false);
      }
      [
        WindowLevelTool.toolName,
        "ContrastTool",
        "Pan",
        "Zoom",
        "PlanarRotate",
        "TrackballRotate",
      ].forEach((toolName) => {
        try {
          toolGroup.setToolPassive(toolName);
        } catch {
          // Tool may not exist on this group.
        }
      });

      if (isOrthographic && mprReady) {
        setCrosshairsEnabled(true);
      }
      const isWNL =
        activeTool === WindowLevelTool.toolName || activeTool === "WindowLevel";
      const isContrast = activeTool === "ContrastTool" || activeTool === "Contrast";
      const isRotate = ["RotateTool", "Rotate", "PlanarRotate", "TrackballRotate"].includes(
        activeTool
      );
      try {
        if (isWNL) {
          toolGroup.setToolActive(WindowLevelTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
        } else if (isContrast) {
          toolGroup.setToolActive("ContrastTool", {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
        } else if (isRotate) {
          const rotateTool = isOrthographic ? "TrackballRotate" : "PlanarRotate";
          toolGroup.setToolActive(rotateTool, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
        } else if (activeTool === "CrosshairsTool" && isOrthographic && mprReady) {
          setCrosshairsEnabled(true);
        } else if (
          isOrthographic &&
          mprReady &&
          (activeTool === "MPRTool" ||
            activeTool === "MPR_THREE_VIEW" ||
            activeTool === "StackScrollTool" ||
            !activeTool)
        ) {
          setCrosshairsEnabled(true);
        } else if (activeTool && !String(activeTool).startsWith("MPR")) {
          toolGroup.setToolActive(activeTool, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
        }
      } catch (error) {
        console.warn("Tool activation failed:", activeTool, error);
      }
    };

    bindTools();
    return () => {
      cancelled = true;
    };
  }, [activeTool, viewportId, isMPR, sharedVolumeId, mprReady]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    let rafId = null;

    const getViewport = async () => {
      const engine = await getGlobalRenderingEngine();
      return engine.getViewport(viewportId);
    };

    const handleKeyDown = async (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();

      const viewport = await getViewport();
      if (!viewport) return;

      const delta = event.key === "ArrowUp" ? -1 : 1;

      if (viewport.type === CoreEnums.ViewportType.STACK) {
        const current = viewport.getCurrentImageIdIndex();
        const count = viewport.getImageIds()?.length || 0;
        const next = Math.max(0, Math.min(count - 1, current + delta));
        if (next !== current) {
          await viewport.setImageIdIndex(next);
        }
      } else {
        moveMPRSlice(viewport, delta);
      }
    };

    const handleWheel = (event) => {
      event.preventDefault();
      if (rafId) cancelAnimationFrame(rafId);

      const direction = event.deltaY > 0 ? 1 : -1;
      rafId = requestAnimationFrame(async () => {
        const viewport = await getViewport();
        if (viewport?.type === CoreEnums.ViewportType.ORTHOGRAPHIC) {
          moveMPRSlice(viewport, direction);
        }
      });
    };
    element.addEventListener("keydown", handleKeyDown);
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      element.removeEventListener("keydown", handleKeyDown);
      element.removeEventListener("wheel", handleWheel);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [viewportId, moveMPRSlice]);

  return (
    <div className="topelementref" onDragOver={handleDragOver} onDrop={handleDrop}>
      {showLoader && (
        <div className="viewport-loader-overlay">
          {!mprBlocked && <div className="spinner" />}
          <span>
            {loadNote || (isMPR ? "Loading MPR volume..." : "Loading images...")}
          </span>
        </div>
      )}

      <div
        className="elementref"
        ref={(node) => {
          elementRef.current = node;
          onElementReady?.(panelIndex, node);
        }}
        tabIndex={0}
        onClick={(event) => {
          event.currentTarget.focus();
          setActiveViewportId(viewportId);
        }}
      />

      {isFirst && (
        <button onClick={onBack} className="backBtn">
          ←
        </button>
      )}

      <div className="topleftcorner">
        <div>{patientInfo.patientName || "Patient Name"}</div>
        <div>{patientInfo.studyDesc || patientInfo.seriesDesc}</div>
      </div>

      <div className="bottomleftcorner">
        <div>Ser: {seriesIndex !== undefined ? seriesIndex + 1 : 1}</div>
        <div ref={sliceTextRef}>Img: 1/{totalSlices}</div>
        {imageSize && <div>{imageSize}</div>}
        {thickness && <div>Thick: {thickness}</div>}
      </div>

      <PanelOverlayTools viewportId={viewportId} totalSlices={totalSlices} />
    </div>
  );
}

export default function ViewerCanvas({
  selectedSeries, seriesIndex, panelSeries = {}, activeTool, layout = DEFAULT_LAYOUT, mprView, onBack
}) {
  const rows = layout?.rows ?? 1;
  const cols = layout?.cols ?? 3;
  const totalPanels = rows * cols;
  const isMPR = isMPRMode(activeTool, mprView);
  const seriesKey = getSeriesKey(selectedSeries);

  const selectedSeriesRef = useRef(selectedSeries);
  selectedSeriesRef.current = selectedSeries;
  const imageCount = selectedSeries?.imageIds?.length || 0;

  const [panelSeriesState, setPanelSeriesState] = useState({});
  const [sharedVolumeId, setSharedVolumeId] = useState(null);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [mprReady, setMprReady] = useState(false);
  const [mprBlocked, setMprBlocked] = useState(false);
  const [loadNote, setLoadNote] = useState("");
  const panelElementsRef = useRef([]);

  const handlePanelDrop = useCallback((panelIndex, series) => {
    setPanelSeriesState((previous) => ({
      ...previous,
      [panelIndex]: { series, index: panelIndex },
    }));
  }, []);

  useEffect(() => {
    setPanelSeriesState({});
  }, [rows, cols]);

  const handleElementReady = useCallback((index, node) => {
    panelElementsRef.current[index] = node;
  }, []);

  const isExtentError = useCallback(
    (error) => String(error?.message || error || "").toLowerCase().includes("extent"),
    []
  );

  const ensureMprViewports = useCallback(
    async (engine) => {
      for (let attempt = 0; attempt < 40; attempt++) {
        const elements = Array.from(
          { length: totalPanels },
          (_, index) => panelElementsRef.current[index]
        );
        if (elements.every(Boolean)) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      for (let index = 0; index < totalPanels; index++) {
        const element = panelElementsRef.current[index];
        if (!element) {
          throw new Error(`MPR panel ${index} has no canvas element`);
        }

        const viewportId = `VIEWPORT_${index}`;
        const existing = engine.getViewport(viewportId);

        if (existing && existing.type !== CoreEnums.ViewportType.ORTHOGRAPHIC) {
          removeViewportFromToolGroups(viewportId);
          engine.disableElement(viewportId);
        }

        if (!engine.getViewport(viewportId)) {
          engine.enableElement({
            viewportId,
            type: CoreEnums.ViewportType.ORTHOGRAPHIC,
            element,
            defaultOptions: { background: [0, 0, 0] },
          });
        }

        const viewport = engine.getViewport(viewportId);
        if (!viewport) {
          throw new Error(`Failed to create ${viewportId}`);
        }

        attachWebGLRecovery(engine, viewportId);
        createToolGroup(viewportId, engine.id, true);
      }
    },
    [totalPanels]
  );

  const bindVolumeToPanels = useCallback(
    async (engine, volumeId, view, { resetCamera = false, isCancelled = () => false } = {}) => {
      const volume = cache.getVolume(volumeId);
      const imageIds = volume?.imageIds || [];
      const firstImageId = imageIds[0];
      const voi = firstImageId ? metaData.get("voiLutModule", firstImageId) || {} : {};
      let wc = Array.isArray(voi.windowCenter) ? voi.windowCenter[0] : voi.windowCenter;
      let ww = Array.isArray(voi.windowWidth) ? voi.windowWidth[0] : voi.windowWidth;
      wc = Number(wc);
      ww = Number(ww);
      if (!Number.isFinite(wc) || !Number.isFinite(ww) || ww <= 0) {
        wc = 40;
        ww = 400;
      }

      for (let index = 0; index < totalPanels; index++) {
        if (isCancelled()) return false;
        const viewport = engine.getViewport(`VIEWPORT_${index}`);
        if (!viewport || viewport.type !== CoreEnums.ViewportType.ORTHOGRAPHIC) {
          continue;
        }

        try {
          await viewport.setVolumes([{ volumeId }], false, true);
        } catch (error) {
          if (!isExtentError(error)) {
            console.warn("setVolumes failed:", error);
          }
          continue;
        }

        if (resetCamera) {
          try {
            const orientation = getViewportOrientation(view || "THREE_VIEW", index);
            if (orientation) viewport.setOrientation(orientation);
          } catch (error) {
            if (!isExtentError(error)) {
              console.warn("setOrientation failed:", error);
            }
          }

          try {
            viewport.setProperties(
              {
                voiRange: { lower: wc - ww / 2, upper: wc + ww / 2 },
                colormap: { name: "Grayscale" },
                interpolationType: 1,
              },
              volumeId
            );
          } catch {
            // Properties can wait until scalar data exists.
          }

          try {
            viewport.resetCamera({
              resetPan: true,
              resetZoom: true,
              resetToCenter: true,
            });
          } catch {
            safeResetCamera(viewport);
          }
        }

        try {
          viewport.render();
        } catch {
          // Render can fail before the first frames decode.
        }
      }

      return true;
    },
    [totalPanels, isExtentError]
  );

  useEffect(() => {
    let cancelled = false;
    const loadVolume = async () => {
      if (!isMPR || !selectedSeriesRef.current?.imageIds?.length || !seriesKey) {
        setSharedVolumeId(null);
        setVolumeLoading(false);
        setMprReady(false);
        setMprBlocked(false);
        setLoadNote("");
        setCrosshairsEnabled(false);
        if (!isMPR) {
          try {
            const engine = await getGlobalRenderingEngine();
            await releaseCurrentVolume(engine);
          } catch {
            // Engine may not be ready yet.
          }
        }
        return;
      }

   
      if (imageCount < MIN_SLICES_FOR_MPR) {
        setSharedVolumeId(null);
        setVolumeLoading(false);
        setMprReady(false);
        setMprBlocked(true);
        setCrosshairsEnabled(false);
        setLoadNote(
          `This series has only ${imageCount} image${imageCount === 1 ? "" : "s"} — ` +
            `at least ${MIN_SLICES_FOR_MPR} are needed to build MPR views. ` +
            `Switch to Stack view for this series instead.`
        );
        return;
      }

      setVolumeLoading(true);
      setMprReady(false);
      setMprBlocked(false);
      setLoadNote("Preparing MPR volume...");
      setCrosshairsEnabled(false);

      try {
        let engine = await ensureGpuRenderingEngine();
        if (cancelled) return;

        const switchingSeries =
          Boolean(getCurrentSeriesKey()) && getCurrentSeriesKey() !== seriesKey;

        if (switchingSeries) {
          for (let index = 0; index < 8; index++) {
            const viewportId = `VIEWPORT_${index}`;
            try {
              removeViewportFromToolGroups(viewportId);
              if (engine.getViewport(viewportId)) {
                engine.disableElement(viewportId);
              }
            } catch {
              // Viewport may already be gone.
            }
          }
          await releaseCurrentVolume(engine);
          engine = await recycleRenderingEngine();
        }

        await ensureMprViewports(engine);
        if (cancelled) return;
        const rawImageIds = buildImageIds(selectedSeriesRef.current.imageIds);
     
        const sortedImageIds = await sortImageIdsAsync(rawImageIds);
      
        if (cancelled) return;

        const result = await loadSharedSeriesVolume({
          engine,
          seriesKey,
          imageIds: sortedImageIds,
        });
        
        if (cancelled || !result || !isVolumeLoadCurrent(result.generation)) {
          return;
        }

        if (result.usedSliceCount < result.totalSliceCount) {
          setLoadNote(
            `Loading ${result.usedSliceCount} of ${result.totalSliceCount} slices for 3-view...`
          );
        } else {
          setLoadNote(`Loading ${result.usedSliceCount} slices for 3-view...`);
        }

        await bindVolumeToPanels(engine, result.volumeId, mprView, {
          resetCamera: false,
          isCancelled: () => cancelled,
        });
        if (cancelled) return;

        await startVolumePixelLoad(result.volume);
        await waitForLoadedFrames(result.volume, 1, 8000);
        if (cancelled) return;

        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );

        try {
          engine.resize(true, false);
        } catch (error) {
          if (!String(error?.message || "").includes("extent")) {
            console.warn("MPR resize failed:", error);
          }
        }

        await bindVolumeToPanels(engine, result.volumeId, mprView, {
          resetCamera: true,
          isCancelled: () => cancelled,
        });

        engine.getViewports().forEach((viewport) => {
          try {
            viewport.render();
          } catch {
            // Ignore.
          }
        });

        setSharedVolumeId(result.volumeId);
        setMprReady(true);
        setLoadNote("");
        setTimeout(() => {
          if (!cancelled) setCrosshairsEnabled(true);
        }, 800);
      } catch (error) {
        if (!cancelled) {
          console.error("Shared MPR volume load failed:", error);
          setSharedVolumeId(null);
          setMprReady(false);
          const message = String(error?.message || "");
          setLoadNote(
            message.toLowerCase().includes("extent")
              ? "Building 3-view volume... if this stays, pick the series again."
              : message || "Failed to load 3-view. Try another series."
          );
        }
      } finally {
        if (!cancelled) setVolumeLoading(false);
      }
    };

    loadVolume();

    return () => {
      cancelled = true;
      setCrosshairsEnabled(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMPR, seriesKey, imageCount, totalPanels]);

  useEffect(() => {
    if (!isMPR || !mprReady || !sharedVolumeId) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const engine = await getGlobalRenderingEngine();
        if (cancelled) return;

        await bindVolumeToPanels(engine, sharedVolumeId, mprView, {
          resetCamera: true,
          isCancelled: () => cancelled,
        });
        if (cancelled) return;

        engine.getViewports().forEach((viewport) => {
          try {
            viewport.render();
          } catch {
            // Ignore transient render errors.
          }
        });
      } catch (error) {
        console.warn("MPR view switch failed:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mprView, isMPR, mprReady, sharedVolumeId, bindVolumeToPanels]);

  useEffect(() => {
    return () => {
      cancelPendingVolumeLoads();
      getGlobalRenderingEngine()
        .then((engine) => detachVolumeFromEngine(engine, sharedVolumeId))
        .catch(() => {});
    };
  }, []);

  useEffect(() => {
    let timer = null;
    let cancelled = false;

    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (cancelled) return;
        try {
          const engine = await getGlobalRenderingEngine();
          if (cancelled) return;
          engine.resize(true, true);
          engine.getViewports().forEach((viewport) => {
            try {
              viewport.render();
            } catch {
              // Ignore.
            }
          });
        } catch {
          // Ignore resize race.
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    const grid = document.querySelector(".viewer-grid");
    const observer =
      grid && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(handleResize)
        : null;
    observer?.observe(grid);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", handleResize);
      observer?.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const panels = useMemo(
    () => Array.from({ length: totalPanels }, (_, index) => index),
    [totalPanels]
  );

  return (
    <div
      className="viewer-grid"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: "2px",
        height: "100%",
        width: "100%",
        backgroundColor: "#000",
      }}
    >
      {panels.map((index) => {
        const seriesToUse = isMPR
          ? selectedSeries
          : panelSeriesState[index]?.series ||
            panelSeries[index]?.series ||
            selectedSeries;

        return (
          <ViewportPanel
            key={`vp-${index}-${isMPR ? "mpr" : "stack"}`}
            panelIndex={index}
            selectedSeries={seriesToUse}
            seriesIndex={
              panelSeriesState[index]?.index ??
              panelSeries[index]?.index ??
              seriesIndex
            }
            onBack={onBack}
            mprView={mprView || (isMPR ? "THREE_VIEW" : null)}
            isMPR={isMPR}
            activeTool={activeTool}
            layout={{ rows, cols }}
            isFirst={index === 0}
            sharedVolumeId={sharedVolumeId}
            volumeLoading={volumeLoading}
            mprReady={mprReady}
            mprBlocked={mprBlocked}
            loadNote={loadNote}
            onElementReady={handleElementReady}
            onSeriesDrop={(series) => handlePanelDrop(index, series)}
          />
        );
      })}
    </div>
  );
}
